// askSmart-ui/src/pages/TOSP/portal/tenantExpensesApi.ts
import { BudgetStatementSnapshot } from "../types";

// The tenant's own expense statements.
//
// Its own endpoint rather than the generic collection helpers, because what
// comes back is not a collection: it is the caller's statements, lifted out of
// the budgets they are entitled to see. The portal does not work expenses out
// any more — it renders what this returns — so the figures a tenant reads are
// the ones the admin published, adjustments included.

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export interface PublishedExpenseYear {
  year: number;
  /** When the figures were frozen. */
  publishedAt?: string;
  statements: BudgetStatementSnapshot[];
  /**
   * The frozen charges for the caller's OWN units, where a space of theirs
   * changed hands inside the year — unitId → twelve monthly figures.
   *
   * Sent only for the units the caller holds, and only for a year where the
   * division is needed, so the portal is never handed the rest of the centre's
   * figures. It is what lets a handover set up after publication show on the
   * right statement without the year being republished: the figures are the
   * published ones, and only who held the space is read live.
   */
  perUnit?: {
    cam: Record<string, number[]>;
    taxes: Record<string, number[]>;
    dues: Record<string, number[]>;
    royalty: Record<string, number[]>;
    charged: Record<string, string>;
  };
}

/**
 * Every published-and-broadcasting year the caller has a statement on, newest
 * first. An empty list is the ordinary answer, not an error: nothing has been
 * released to them yet.
 */
export async function loadMyExpenses(): Promise<PublishedExpenseYear[]> {
  const res = await fetch(`${BACKEND}/tosp/my-expenses`, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(body?.error || `Couldn't load your expenses (${res.status})`);
  }
  return Array.isArray(body.years) ? body.years : [];
}
